import { useT } from '@/shared/lib/i18n';
import { EyeIcon } from '../icons/EyeIcon';
import { Postit } from '../Postit';

export type RoomCodePostitProps = {
  code: string;
  /** Postit width — desktop default 130, mobile default 120. */
  width?: number;
  /** Number font size — desktop default 28, mobile default 22. */
  numberSize?: number;
  /** When true, mask the code as dots so spectators can't read it. */
  hidden?: boolean;
  /** Show an eye toggle (host-only). When set, clicking calls `onToggleHidden`. */
  onToggleHidden?: () => void;
};

const MASK_CHAR = '•';

/**
 * Yellow code postit displayed in the lobby top bar (online only). When
 * `onToggleHidden` is provided (host gate), an ink-stroke eye chip is
 * rendered as a wax-stamp pinned to the bottom-right corner of the
 * postit — outside the label/number stack so it never overlaps the copy.
 */
export const RoomCodePostit = ({
  code,
  width = 130,
  numberSize = 28,
  hidden,
  onToggleHidden,
}: RoomCodePostitProps) => {
  const t = useT();
  const display = hidden ? MASK_CHAR.repeat(code.length) : code;
  return (
    <div className="relative inline-flex">
      <Postit tone="yellow" rot={-3} w={width} withPin>
        <div className="font-type text-[9px] tracking-[0.22em] text-ink/55 text-center">
          {t('online.room.code_label')}
        </div>
        <div
          className="font-display font-extrabold tracking-[0.18em] text-ink leading-none mt-1 text-center"
          style={{ fontSize: numberSize }}
        >
          {display}
        </div>
      </Postit>
      {onToggleHidden && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleHidden();
          }}
          aria-label={t(hidden ? 'online.room.code_show' : 'online.room.code_hide')}
          aria-pressed={hidden}
          title={t(hidden ? 'online.room.code_show' : 'online.room.code_hide')}
          className="-right-3 -bottom-2 absolute z-10 flex h-8 w-8 items-center justify-center rounded-full border-[1.5px] border-ink/85 bg-cream text-ink transition hover:scale-105 active:scale-95"
          style={{
            transform: 'rotate(8deg)',
            boxShadow: '0 2px 0 rgba(0,0,0,0.18), 0 4px 10px rgba(0,0,0,0.18)',
          }}
        >
          <EyeIcon size={16} off={hidden} />
        </button>
      )}
    </div>
  );
};
